// src/pages/projects/ProjectNotesPage.tsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MessageSquare,
  Plus,
  Pin,
  Reply,
  MoreVertical,
  X,
  Search,
  Filter,
  Calendar,
  User,
  CheckCircle,
  AlertCircle,
  Info,
  FileText,
  Send,
  ThumbsUp,
  Heart,
  Star,
  Paperclip,
  Edit2,
  Trash2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { projectNotesService } from '../../services/projectNotes.service';
import { ProjectNote, NoteType, NoteVisibility, DutyAcceptance } from '../../types/projectNotes.types';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';

const ProjectNotesPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { selectedProject, projects, setSelectedProjectId } = useProject();
  const [searchParams] = useSearchParams();

  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<ProjectNote | null>(null);
  const [showReplies, setShowReplies] = useState<string | null>(null);
  const [replies, setReplies] = useState<Record<string, ProjectNote[]>>({});
  const [filterType, setFilterType] = useState<NoteType | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const isProvider = userProfile?.role === 'provider_admin' || userProfile?.role === 'provider_user';
  const isAdmin = userProfile?.role === 'provider_admin';

  // Set project from URL if provided
  useEffect(() => {
    const projectId = searchParams.get('projectId');
    if (projectId && projects.length > 0) {
      setSelectedProjectId(projectId);
    }
  }, [searchParams, projects, setSelectedProjectId]);

  useEffect(() => {
    if (selectedProject && isProvider) {
      loadNotes();
    }
  }, [selectedProject, filterType, userProfile, isProvider]);

  const loadNotes = async () => {
    if (!selectedProject || !userProfile || !currentUser || !isProvider) return;

    setLoading(true);
    try {
      const noteList = await projectNotesService.getNotes(
        {
          projectId: selectedProject.id,
          type: filterType === 'all' ? undefined : [filterType],
          parentNoteId: null
        },
        userProfile.role,
        currentUser.uid
      );
      setNotes(noteList);
    } catch (error) {
      console.error('Error loading notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const loadReplies = async (noteId: string) => {
    if (!userProfile || !currentUser || !isProvider) return;

    try {
      const replyList = await projectNotesService.getNoteReplies(noteId, userProfile.role, currentUser.uid);
      setReplies(prev => ({ ...prev, [noteId]: replyList }));
    } catch (error) {
      console.error('Error loading replies:', error);
    }
  };

  // Project notes are provider-side only - check after all hooks
  if (!isProvider) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-600">Access Restricted</h2>
        <p className="text-gray-500 mt-2">Project notes are only available to provider team members.</p>
      </div>
    );
  }

  const handleToggleReplies = async (noteId: string) => {
    if (showReplies === noteId) {
      setShowReplies(null);
    } else {
      setShowReplies(noteId);
      if (!replies[noteId]) {
        await loadReplies(noteId);
      }
    }
  };

  const handleTogglePin = async (note: ProjectNote) => {
    if (!currentUser) return;

    try {
      await projectNotesService.togglePinNote(note.id, !note.isPinned, currentUser.uid);
      toast.success(note.isPinned ? 'Note unpinned' : 'Note pinned');
      loadNotes();
    } catch (error) {
      console.error('Error toggling pin:', error);
      toast.error('Failed to update note');
    }
  };

  const handleReaction = async (noteId: string, emoji: string) => {
    if (!currentUser) return;

    try {
      const note = notes.find(n => n.id === noteId);
      const hasReacted = note?.reactions?.[emoji]?.includes(currentUser.uid);

      if (hasReacted) {
        await projectNotesService.removeReaction(noteId, emoji, currentUser.uid);
      } else {
        await projectNotesService.addReaction(noteId, emoji, currentUser.uid);
      }
      loadNotes();
    } catch (error) {
      console.error('Error updating reaction:', error);
    }
  };

  const getNoteTypeIcon = (type: NoteType) => {
    switch (type) {
      case 'duty_acceptance':
        return <CheckCircle className="h-4 w-4 text-accent-500" />;
      case 'issue':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'resolution':
        return <CheckCircle className="h-4 w-4 text-accent-500" />;
      case 'update':
        return <Info className="h-4 w-4 text-secondary-500" />;
      case 'client_feedback':
        return <Star className="h-4 w-4 text-coral-500" />;
      case 'internal':
        return <FileText className="h-4 w-4 text-primary-500" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-500" />;
    }
  };

  const getNoteTypeBadge = (type: NoteType) => {
    const styles: Record<NoteType, string> = {
      general: 'bg-gray-100 text-gray-700',
      duty_acceptance: 'bg-accent-100 text-accent-700',
      update: 'bg-secondary-100 text-secondary-700',
      issue: 'bg-red-100 text-red-700',
      resolution: 'bg-accent-100 text-accent-700',
      client_feedback: 'bg-coral-100 text-coral-600',
      internal: 'bg-primary-100 text-primary-700'
    };

    const labels: Record<NoteType, string> = {
      general: 'General',
      duty_acceptance: 'Duty Acceptance',
      update: 'Update',
      issue: 'Issue',
      resolution: 'Resolution',
      client_feedback: 'Client Feedback',
      internal: 'Internal'
    };

    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[type]}`}>
        {labels[type]}
      </span>
    );
  };

  const getVisibilityBadge = (visibility: NoteVisibility) => {
    if (visibility === 'all') return null;

    const styles: Record<NoteVisibility, string> = {
      all: '',
      admins_only: 'bg-primary-100 text-primary-700',
      provider_only: 'bg-coral-100 text-coral-600'
    };

    const labels: Record<NoteVisibility, string> = {
      all: '',
      admins_only: 'Admins Only',
      provider_only: 'Provider Only'
    };

    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[visibility]}`}>
        {labels[visibility]}
      </span>
    );
  };

  const filteredNotes = notes.filter(note => {
    if (searchTerm) {
      return (
        note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  // Separate pinned and unpinned notes
  const pinnedNotes = filteredNotes.filter(n => n.isPinned);
  const regularNotes = filteredNotes.filter(n => !n.isPinned);

  if (!selectedProject) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">Select a Project</h3>
          <p className="text-gray-500">Please select a project to view notes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Project Notes</h1>
          <p className="text-gray-500 mt-1">{selectedProject.name}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-200"
        >
          <Plus className="h-5 w-5" />
          Add Note
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Types</option>
            <option value="general">General</option>
            <option value="update">Updates</option>
            <option value="issue">Issues</option>
            <option value="resolution">Resolutions</option>
            <option value="duty_acceptance">Duty Acceptance</option>
            <option value="client_feedback">Client Feedback</option>
            <option value="internal">Internal</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Pinned Notes */}
          {pinnedNotes.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                <Pin className="h-4 w-4" />
                Pinned Notes
              </h3>
              {pinnedNotes.map(note => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onTogglePin={handleTogglePin}
                  onToggleReplies={handleToggleReplies}
                  onReaction={handleReaction}
                  showReplies={showReplies === note.id}
                  replies={replies[note.id]}
                  isAdmin={isAdmin}
                  currentUserId={currentUser?.uid || ''}
                  getNoteTypeIcon={getNoteTypeIcon}
                  getNoteTypeBadge={getNoteTypeBadge}
                  getVisibilityBadge={getVisibilityBadge}
                  onRefresh={loadNotes}
                />
              ))}
            </div>
          )}

          {/* Regular Notes */}
          {regularNotes.length === 0 && pinnedNotes.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">No notes yet</h3>
              <p className="text-gray-500">Be the first to add a note to this project.</p>
            </div>
          ) : (
            regularNotes.map(note => (
              <NoteCard
                key={note.id}
                note={note}
                onTogglePin={handleTogglePin}
                onToggleReplies={handleToggleReplies}
                onReaction={handleReaction}
                showReplies={showReplies === note.id}
                replies={replies[note.id]}
                isAdmin={isAdmin}
                currentUserId={currentUser?.uid || ''}
                getNoteTypeIcon={getNoteTypeIcon}
                getNoteTypeBadge={getNoteTypeBadge}
                getVisibilityBadge={getVisibilityBadge}
                onRefresh={loadNotes}
              />
            ))
          )}
        </div>
      )}

      {/* Create Note Modal */}
      {showCreateModal && (
        <CreateNoteModal
          projectId={selectedProject.id}
          projectName={selectedProject.name}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadNotes();
          }}
        />
      )}
    </div>
  );
};

// Note Card Component
interface NoteCardProps {
  note: ProjectNote;
  onTogglePin: (note: ProjectNote) => void;
  onToggleReplies: (noteId: string) => void;
  onReaction: (noteId: string, emoji: string) => void;
  showReplies: boolean;
  replies?: ProjectNote[];
  isAdmin: boolean;
  currentUserId: string;
  getNoteTypeIcon: (type: NoteType) => React.ReactNode;
  getNoteTypeBadge: (type: NoteType) => React.ReactNode;
  getVisibilityBadge: (visibility: NoteVisibility) => React.ReactNode;
  onRefresh: () => void;
}

const NoteCard: React.FC<NoteCardProps> = ({
  note,
  onTogglePin,
  onToggleReplies,
  onReaction,
  showReplies,
  replies,
  isAdmin,
  currentUserId,
  getNoteTypeIcon,
  getNoteTypeBadge,
  getVisibilityBadge,
  onRefresh
}) => {
  const reactionEmojis = ['👍', '❤️', '⭐'];

  return (
    <div className={`bg-white rounded-xl shadow-sm border ${note.isPinned ? 'border-primary-200 bg-primary-50/30' : 'border-gray-100'} overflow-hidden`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-medium">
              {note.authorName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-800">{note.authorName}</span>
                <span className="text-xs text-gray-400">
                  {format(note.createdAt.toDate(), 'MMM d, yyyy h:mm a')}
                </span>
                {note.isEdited && (
                  <span className="text-xs text-gray-400">(edited)</span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                {getNoteTypeBadge(note.type)}
                {getVisibilityBadge(note.visibility)}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {note.isPinned && (
              <Pin className="h-4 w-4 text-primary-500" />
            )}
            {(isAdmin || note.authorId === currentUserId) && (
              <button
                onClick={() => onTogglePin(note)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title={note.isPinned ? 'Unpin' : 'Pin'}
              >
                <Pin className={`h-4 w-4 ${note.isPinned ? 'text-primary-500' : 'text-gray-400'}`} />
              </button>
            )}
          </div>
        </div>

        {/* Title */}
        {note.title && (
          <h4 className="font-semibold text-gray-800 mb-2">{note.title}</h4>
        )}

        {/* Content */}
        <p className="text-gray-600 whitespace-pre-wrap">{note.content}</p>

        {/* Attachments */}
        {note.attachments && note.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {note.attachments.map((attachment, idx) => (
              <a
                key={idx}
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg text-sm text-gray-600 hover:bg-gray-200 transition-colors"
              >
                <Paperclip className="h-4 w-4" />
                {attachment.name}
              </a>
            ))}
          </div>
        )}

        {/* Reactions & Actions */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            {reactionEmojis.map(emoji => {
              const count = note.reactions?.[emoji]?.length || 0;
              const hasReacted = note.reactions?.[emoji]?.includes(currentUserId);

              return (
                <button
                  key={emoji}
                  onClick={() => onReaction(note.id, emoji)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-colors ${
                    hasReacted
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span>{emoji}</span>
                  {count > 0 && <span>{count}</span>}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => onToggleReplies(note.id)}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 transition-colors"
          >
            <Reply className="h-4 w-4" />
            {note.replyCount > 0 ? `${note.replyCount} replies` : 'Reply'}
          </button>
        </div>
      </div>

      {/* Replies */}
      {showReplies && (
        <div className="bg-gray-50 border-t border-gray-100 p-4">
          {replies && replies.length > 0 ? (
            <div className="space-y-3">
              {replies.map(reply => (
                <div key={reply.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-secondary-400 to-secondary-600 flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                    {reply.authorName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-gray-800">{reply.authorName}</span>
                      <span className="text-xs text-gray-400">
                        {format(reply.createdAt.toDate(), 'MMM d, h:mm a')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{reply.content}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center">No replies yet</p>
          )}

          <ReplyInput
            parentNoteId={note.id}
            projectId={note.projectId}
            projectName={note.projectName}
            onReplied={() => {
              onRefresh();
              onToggleReplies(note.id);
              setTimeout(() => onToggleReplies(note.id), 100);
            }}
          />
        </div>
      )}
    </div>
  );
};

// Reply Input Component
interface ReplyInputProps {
  parentNoteId: string;
  projectId: string;
  projectName: string;
  onReplied: () => void;
}

const ReplyInput: React.FC<ReplyInputProps> = ({ parentNoteId, projectId, projectName, onReplied }) => {
  const { currentUser, userProfile } = useAuth();
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || !currentUser || !userProfile) return;

    setSubmitting(true);
    try {
      await projectNotesService.createNote(
        {
          projectId,
          type: 'general',
          content,
          visibility: 'all',
          parentNoteId
        },
        projectName,
        currentUser.uid,
        userProfile.name || currentUser.displayName || 'Unknown',
        userProfile.role
      );

      setContent('');
      onReplied();
      toast.success('Reply added');
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error('Failed to add reply');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 mt-3">
      <input
        type="text"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Write a reply..."
        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
      />
      <button
        type="submit"
        disabled={!content.trim() || submitting}
        className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Send className="h-4 w-4" />
      </button>
    </form>
  );
};

// Create Note Modal
interface CreateNoteModalProps {
  projectId: string;
  projectName: string;
  onClose: () => void;
  onCreated: () => void;
}

const CreateNoteModal: React.FC<CreateNoteModalProps> = ({
  projectId,
  projectName,
  onClose,
  onCreated
}) => {
  const { currentUser, userProfile } = useAuth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<NoteType>('general');
  const [visibility, setVisibility] = useState<NoteVisibility>('provider_team');
  const [submitting, setSubmitting] = useState(false);

  const isProvider = userProfile?.role?.startsWith('provider');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || !currentUser || !userProfile) return;

    setSubmitting(true);
    try {
      await projectNotesService.createNote(
        {
          projectId,
          type,
          title: title.trim() || undefined,
          content,
          visibility
        },
        projectName,
        currentUser.uid,
        userProfile.name || currentUser.displayName || 'Unknown',
        userProfile.role
      );

      toast.success('Note created');
      onCreated();
    } catch (error) {
      console.error('Error creating note:', error);
      toast.error('Failed to create note');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">Add Note</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Note title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              required
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Write your note..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as NoteType)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="general">General</option>
                <option value="update">Update</option>
                <option value="issue">Issue</option>
                <option value="resolution">Resolution</option>
                <option value="internal">Internal</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as NoteVisibility)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="provider_team">Provider Team</option>
                <option value="me_only">Me Only</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!content.trim() || submitting}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Add Note
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectNotesPage;
